import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { BoundingBox, MapLayer, Position } from 'components/map/types';
import type { RootState } from 'store/reducers';

import type { Camera, Vehicle } from 'API';

export interface RouteLocation {
    id: string;
    label: string;
    position: Position;
}

interface VehicleMapState {
    showHubs: boolean;
    mapContainerSizeTimestamp: number;
    mapLayers: MapLayer[];
    boundingBox?: BoundingBox;
}

interface PagesState {
    vehicleDetails: {
        open: boolean;
        reference: string | undefined;
    };
}

interface SelectedVehicleState {
    selectedVIN: Vehicle['vin'] | undefined;
    /**
     * True right after the vehicle was picked from the vehicle list, the search box or the Priority
     * Vehicle List (PVL) — as opposed to being clicked as a marker on the map, which sets it false.
     *
     * This is a one-shot: it asks for a single camera move, so whoever reacts to it must clear it
     * again with `clearPVLTrigger`. Do not read it as "this vehicle came from the PVL", because
     * after the camera move it is false even though the vehicle did come from there.
     */
    pvlTriggered: boolean | undefined;
}

export enum SelectedDeviationTab {
    ACTIVE = 'active',
    RESOLVED = 'resolved',
}

export interface AppState {
    sessionExpiredDialogOpen: boolean;
    map: VehicleMapState;
    selectedVehicleState: SelectedVehicleState;
    followSelectedVehicle: boolean; // todo: review variable name
    isFullMissionRouteVisible: boolean; // todo: review variable name
    pages: PagesState;
    isCameraStreamMaximized: boolean;
    selectedCamera?: Camera;
    selectedDeviationTab: SelectedDeviationTab;
    actualDeviationParentId: string | undefined;
}

const initialState: AppState = {
    sessionExpiredDialogOpen: false,
    map: {
        boundingBox: undefined,
        showHubs: true,
        mapContainerSizeTimestamp: Date.now(),
        mapLayers: [],
    },
    selectedVehicleState: {
        selectedVIN: undefined,
        pvlTriggered: undefined,
    },
    followSelectedVehicle: false,
    isFullMissionRouteVisible: false,
    pages: {
        vehicleDetails: {
            open: false,
            reference: undefined,
        },
    },
    isCameraStreamMaximized: false,
    selectedCamera: undefined,
    selectedDeviationTab: SelectedDeviationTab.ACTIVE,
    actualDeviationParentId: undefined,
};

export const appSlice = createSlice({
    name: 'app',
    initialState,
    reducers: {
        resetAppState: () => initialState,
        showSessionExpiredDialog: (state: AppState) => {
            state.sessionExpiredDialogOpen = true;
        },
        hideSessionExpiredDialog: (state: AppState) => {
            state.sessionExpiredDialogOpen = false;
        },
        /**
         * Selects a vehicle. Pass `pvlTriggered: true` when the user picked it from a list (vehicle
         * list, search box, PVL) and the map should move to it, or `false` when they clicked its
         * marker on the map and the viewport should stay where it is. See `clearPVLTrigger`: the
         * flag is a one-shot and gets switched off again once the map has acted on it.
         */
        setSelectedVehicle: (
            state: AppState,
            action: PayloadAction<{ selectedVIN?: Vehicle['vin']; pvlTriggered?: boolean }>
        ) => {
            state.selectedVehicleState = {
                selectedVIN: action.payload.selectedVIN,
                pvlTriggered: action.payload.pvlTriggered,
            };
        },
        clearSelectedVehicle: (state: AppState) => {
            state.selectedVehicleState.selectedVIN = undefined;
            state.selectedVehicleState.pvlTriggered = undefined;
        },
        /**
         * Turns the `pvlTriggered` flag off once something has acted on it.
         *
         * `pvlTriggered` means "the user just picked this vehicle from the vehicle list, the search
         * box or the PVL (Priority Vehicle List)" — see `setSelectedVehicle`. It describes a moment,
         * not a lasting state, so whoever reacts to it has to switch it off again. Leave it on and
         * the map keeps redoing the camera move that should only happen on the click itself.
         */
        clearPVLTrigger: (state: AppState) => {
            state.selectedVehicleState.pvlTriggered = false;
        },
        setFollowSelectedVehicle: (state: AppState, action: PayloadAction<boolean>) => {
            state.followSelectedVehicle = action.payload;
        },
        setIsFullMissionRouteVisible: (state: AppState, action: PayloadAction<boolean>) => {
            state.isFullMissionRouteVisible = action.payload;
        },
        setIsVehicleDetailsOpen: (state: AppState, action: PayloadAction<boolean>) => {
            state.pages.vehicleDetails = { ...state.pages.vehicleDetails, open: action.payload };
        },
        setBoundingBox: (state: AppState, action: PayloadAction<BoundingBox | undefined>) => {
            state.map.boundingBox = action.payload;
        },
        setMapLayers: (state: AppState, action: PayloadAction<MapLayer[]>) => {
            state.map.mapLayers = action.payload;
        },
        setShowHubs: (state: AppState, action: PayloadAction<boolean>) => {
            state.map.showHubs = action.payload;
        },
        setIsCameraStreamMaximized: (state: AppState, action: PayloadAction<boolean>) => {
            state.isCameraStreamMaximized = action.payload;
        },
        setSelectedCamera: (state: AppState, action: PayloadAction<Camera>) => {
            state.selectedCamera = action.payload;
        },
        setVehicleDetailsPageReference: (state: AppState, action: PayloadAction<string | undefined>) => {
            state.pages.vehicleDetails.reference = action.payload;
        },
        setSelectedDeviationTab: (state: AppState, action: PayloadAction<SelectedDeviationTab>) => {
            state.selectedDeviationTab = action.payload;
        },
        setActualDeviationParentId: (state: AppState, action: PayloadAction<string | undefined>) => {
            state.actualDeviationParentId = action.payload;
        },
    },
});

export const {
    resetAppState,
    showSessionExpiredDialog,
    hideSessionExpiredDialog,
    setSelectedVehicle,
    clearSelectedVehicle,
    clearPVLTrigger,
    setIsVehicleDetailsOpen,
    setBoundingBox,
    setMapLayers,
    setShowHubs,
    setFollowSelectedVehicle,
    setIsFullMissionRouteVisible,
    setIsCameraStreamMaximized,
    setSelectedCamera,
    setVehicleDetailsPageReference,
    setSelectedDeviationTab,
    setActualDeviationParentId,
} = appSlice.actions;

// map selectors
export const getBoundingBox = (state: RootState) => state.app.map.boundingBox;
export const getMapContainerSizeTimestamp = (state: RootState) => state.app.map.mapContainerSizeTimestamp;
export const getMapLayers = (state: RootState) => state.app.map.mapLayers;
export const getShowHubs = (state: RootState) => state.app.map.showHubs;

export const getSessionExpiredDialogOpen = (state: RootState) => state.app.sessionExpiredDialogOpen;
export const getSelectedVehicle = (state: RootState) => state.app.selectedVehicleState; // todo: rename action to getSelectedVehicleState
export const getIsPVLTriggered = (state: RootState) => state.app.selectedVehicleState.pvlTriggered;
export const getFollowSelectedVehicle = (state: RootState) => state.app.followSelectedVehicle;
export const getIsFullMissionRouteVisible = (state: RootState) => state.app.isFullMissionRouteVisible;
export const getSelectedCamera = (state: RootState) => state.app.selectedCamera;
export const getIsCameraStreamMaximized = (state: RootState) => state.app.isCameraStreamMaximized;
export const getSelectedDeviationTab = (state: RootState) => state.app.selectedDeviationTab;
export const getActualDeviationParentId = (state: RootState) => state.app.actualDeviationParentId;

// window selectors
export const getIsVehicleDetailsOpen = (state: RootState) => state.app.pages.vehicleDetails.open;
export const getVehicleDetailsPageReference = (state: RootState) => state.app.pages.vehicleDetails.reference;

export default appSlice.reducer;
